import type { Dash } from "plotly.js";
import { indicatorAutoLabel } from "@strat/format";
import type { IndicatorDef, LineStyle } from "@strat/format";

/**
 * Recomputed live from the current params — so editing an indicator's period
 * after adding it (e.g. sma_20 -> period 50) never leaves a stale "sma_20"
 * label behind. `label` overrides this when the user has renamed it.
 *
 * The catalog's short name and every param in catalog order: "MACD(12,26,9)",
 * "KC(20,2,10)". A table per kind here once fell behind the catalog.
 */
type Labelled = Pick<IndicatorDef, "kind" | "params"> &
  Partial<Pick<IndicatorDef, "label" | "timeframe" | "formula" | "source">>;

/**
 * `others` is the strategy's indicator list: a formula is named by what it
 * reads ("EMA(5) − EMA(35)") only when the names of those are at hand.
 */
export function autoIndicatorLabel(
  def: Pick<IndicatorDef, "kind" | "params"> & Partial<Pick<IndicatorDef, "timeframe" | "formula">>,
  others?: readonly IndicatorDef[],
): string {
  return indicatorAutoLabel(def, others);
}

export function indicatorLabel(def: Labelled, others?: readonly IndicatorDef[]): string {
  const custom = def.label?.trim();
  return custom ? custom : autoIndicatorLabel(def, others);
}

/**
 * Label with the market appended when the indicator reads a *different* one —
 * two RSI(14)s on the same strategy are indistinguishable otherwise, and
 * picking the wrong one in a condition is a silent logic error.
 */
export function indicatorDisplayName(
  def: Labelled,
  base?: { symbol: string; timeframe: string; marketType?: string },
  others?: readonly IndicatorDef[],
): string {
  const name = indicatorLabel(def, others);
  const source = def.source;
  if (!source) return name;
  const sameMarket =
    base !== undefined &&
    source.symbol.toUpperCase() === base.symbol.toUpperCase() &&
    source.timeframe === base.timeframe &&
    source.marketType === (base.marketType ?? "spot");
  return sameMarket ? name : `${name} · ${source.symbol} ${source.timeframe}`;
}

export const LINE_STYLE_OPTIONS: { value: LineStyle; label: string }[] = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "dashdot", label: "Dash-dot" },
];

export const PLOTLY_DASH: Record<LineStyle, Dash> = {
  solid: "solid",
  dashed: "dash",
  dotted: "dot",
  dashdot: "dashdot",
};

export const LINE_WIDTH_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Thin" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Thick" },
];

export const DEFAULT_LINE_WIDTH = 1.4;
